import { useEventCallback } from "@/client/ui/hooks/useEventCallback";
import { KeyboardShortcutModalScope } from "@/client/ui/KeyboardShortcutProvider";
import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Modal, Platform } from "react-native";
import { PortalProvider } from "react-native-teleport";
import { View } from "@/client/ui/View";
import type { PressableProps } from "react-native";
import Reanimated, {
  Easing,
  Extrapolation,
  interpolate,
  interpolateColor,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withSpring,
  withTiming,
} from "react-native-reanimated";
import { ReanimatedPressable } from "./ReanimatedPressable";
import { Suspense } from "./Suspense";
import { Theme } from "./Theme";

export type PageSheetChild = (options: { dismiss: () => void }) => ReactNode;

interface PageSheetModalProps {
  children: PageSheetChild;
  disableBackgroundDismiss?: boolean;
  webTopAligned?: boolean;
  onDismissRequest?: (dismiss: () => void) => void;
  /**
   * If `true`, the modal will be presented with a slower animation so it is not
   * as jarring.
   */
  passivePresentation?: boolean;
  onDismiss: () => void;
  /**
   * For development purposes only, this will disable the animations and
   * allow the modal to be rendered immediately.
   * This is useful for snapshot testing.
   */
  devUiSnapshotMode?: boolean;
  /**
   * What the Suspense boundary wrapping the child content should fallback to
   * rendering when there is suspense content.
   */
  suspenseFallback: ReactNode;
}

export const PageSheetModal = ({
  children,
  disableBackgroundDismiss = false,
  webTopAligned = false,
  onDismissRequest,
  onDismiss,
  passivePresentation = false,
  devUiSnapshotMode = false,
  suspenseFallback,
}: PageSheetModalProps) => {
  return (
    <Theme theme="default">
      <KeyboardShortcutModalScope active={!devUiSnapshotMode}>
        <PageSheetModalImpl
          onDismiss={onDismiss}
          onDismissRequest={onDismissRequest}
          passivePresentation={passivePresentation}
          disableBackgroundDismiss={disableBackgroundDismiss}
          webTopAligned={webTopAligned}
          devUiSnapshotMode={devUiSnapshotMode}
        >
          {(api) => (
            <Suspense fallback={suspenseFallback}>{children(api)}</Suspense>
          )}
        </PageSheetModalImpl>
      </KeyboardShortcutModalScope>
    </Theme>
  );
};

type ImplProps = Required<
  Omit<PageSheetModalProps, `suspenseFallback` | `onDismissRequest`>
> & {
  onDismissRequest?: (dismiss: () => void) => void;
};

const WebImpl = ({
  children,
  disableBackgroundDismiss,
  onDismissRequest,
  passivePresentation,
  onDismiss,
  devUiSnapshotMode,
  webTopAligned,
}: ImplProps) => {
  "use memo";

  // snapshot the value so that changes to it don't cause show/dismiss
  // animations to repeat.
  const [passivePresentationSnapshot] = useState(passivePresentation);
  const backgroundAnimation = useSharedValue(0);
  const contentAnimation = useSharedValue(0);
  const [dismissing, setDismissing] = useState(false);
  const startedOnBackgroundRef = useRef(false);

  const api = {
    dismiss: () => {
      setDismissing(true);
    },
  };

  const requestDismiss = useEventCallback(() => {
    if (onDismissRequest != null) {
      onDismissRequest(api.dismiss);
      return;
    }
    api.dismiss();
  });

  const onBackgroundPressIn = useEventCallback<
    NonNullable<PressableProps[`onPressIn`]>
  >((e) => {
    startedOnBackgroundRef.current = e.target === e.currentTarget;
  });

  const onBackgroundPress = useEventCallback<
    NonNullable<PressableProps[`onPress`]>
  >((e) => {
    const startedOnBackground = startedOnBackgroundRef.current;
    startedOnBackgroundRef.current = false;
    // Don't trigger on any bubbling events.
    if (
      !disableBackgroundDismiss &&
      startedOnBackground &&
      e.target === e.currentTarget
    ) {
      requestDismiss();
    }
  });

  useEffect(() => {
    if (dismissing) {
      backgroundAnimation.set(
        withTiming(
          0,
          {
            duration: 500,
            easing: Easing.inOut(Easing.quad),
          },
          () => {
            onDismiss();
          },
        ),
      );

      contentAnimation.set(
        withTiming(0, {
          duration: 150,
          easing: Easing.inOut(Easing.quad),
        }),
      );
    } else {
      backgroundAnimation.set(
        withTiming(1, {
          duration: passivePresentationSnapshot ? 500 : 250,
          easing: Easing.inOut(Easing.quad),
        }),
      );

      contentAnimation.set(
        withDelay(
          passivePresentationSnapshot ? 500 : 0,
          withSpring(1, {
            duration: passivePresentationSnapshot ? 500 : 250,
          }),
        ),
      );
    }
  }, [
    backgroundAnimation,
    contentAnimation,
    dismissing,
    onDismiss,
    passivePresentationSnapshot,
  ]);

  const animatedBackgroundStyle = useAnimatedStyle(() => {
    return {
      backgroundColor: interpolateColor(
        backgroundAnimation.get(),
        [0, 1],
        [`rgba(0,0,0,0)`, `rgba(0,0,0,0.5)`],
      ),
    };
  });

  const animatedContentStyle = useAnimatedStyle(() => {
    return {
      transform: [
        {
          translateY: interpolate(
            contentAnimation.get(),
            [0, 1],
            [20, 0],
            // Extrapolation.CLAMP,
          ),
        },
      ],
      opacity: interpolate(
        contentAnimation.get(),
        [0, 1],
        [0, 1],
        Extrapolation.CLAMP,
      ),
    };
  });

  const content = (
    <Reanimated.View
      className={`
        w-full flex-1 overflow-hidden rounded-t-2xl bg-bg shadow-[0_20px_60px_rgba(0,0,0,0.55)]

        sm:max-h-[80vh] sm:w-125 sm:flex-none sm:rounded-xl
      `}
      style={[animatedContentStyle]}
    >
      {children(api)}
    </Reanimated.View>
  );

  return devUiSnapshotMode ? (
    <PortalProvider>
      <View
        className={`
          size-full cursor-auto items-center bg-[black]/50 px-4

          ${webTopAligned ? `justify-start pt-5 pb-4` : `justify-center py-4`}
        `}
      >
        {content}
      </View>
    </PortalProvider>
  ) : (
    <Modal
      presentationStyle="fullScreen"
      transparent={true}
      onRequestClose={requestDismiss}
    >
      <PortalProvider>
        <ReanimatedPressable
          className={`
            absolute size-full cursor-auto items-center justify-start pt-5

            ${webTopAligned ? `sm:px-4 sm:pb-4` : `sm:justify-center sm:p-4`}
          `}
          style={[animatedBackgroundStyle]}
          onPressIn={onBackgroundPressIn}
          onPress={onBackgroundPress}
        >
          {content}
        </ReanimatedPressable>
      </PortalProvider>
    </Modal>
  );
};

const IosImpl = ({ onDismiss, onDismissRequest, children }: ImplProps) => {
  const api = useMemo(
    () => ({
      dismiss: onDismiss,
    }),
    [onDismiss],
  );

  const requestDismiss = useEventCallback(() => {
    if (onDismissRequest != null) {
      onDismissRequest(api.dismiss);
      return;
    }
    api.dismiss();
  });

  return (
    <Modal
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={requestDismiss}
    >
      <View
        className={
          // On iOS there is 72px of the page sheet content that is off the
          // bottom of the screen. Normally this meant that if there's a
          // `flex-1` view the bottom 72px of its content is cropped off.
          //
          // For debugging the background is set to purple, but this should
          // never be seen. If you see a purple line on iOS then this is the
          // cause and needs to be set more carefully.
          `
            flex-1 pb-18

            ${__DEV__ ? `bg-[purple]` : ``}
          `
        }
      >
        <View className={`flex-1 bg-bg`}>{children(api)}</View>
      </View>
    </Modal>
  );
};

const DefaultImpl = ({ children, onDismiss, onDismissRequest }: ImplProps) => {
  const api = useMemo(
    () => ({
      dismiss: onDismiss,
    }),
    [onDismiss],
  );

  const requestDismiss = useEventCallback(() => {
    if (onDismissRequest != null) {
      onDismissRequest(api.dismiss);
      return;
    }
    api.dismiss();
  });

  return (
    <Modal
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={requestDismiss}
    >
      <View className={`flex-1 bg-bg`}>{children(api)}</View>
    </Modal>
  );
};

const PageSheetModalImpl = Platform.select({
  web: WebImpl,
  ios: IosImpl,
  default: DefaultImpl,
});
